/**
 * PromoCampaignPanel — embedded in the Campaigns (Sequences) page.
 * Two-column layout with create, edit, preview, and send.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EmailEditor } from '@/components/email/EmailEditor';
import { TemplatePickerDialog } from '@/components/email/TemplatePickerDialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Music, Send, Play, Heart, Download, Trash2,
  CheckCircle2, Users, ExternalLink, Search, Upload, Link,
  LayoutTemplate, Pencil, Eye,
} from 'lucide-react';
import { promoAPI, contactsAPI, sequencesAPI, socialMediaAPI } from '@/lib/api';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';

// ── Stat chip ─────────────────────────────────────────────────────────────────
const Stat = ({ icon: Icon, count, label, color }) => (
  <div className="flex flex-col items-center gap-0.5 p-3 rounded-lg bg-[#FBF8F2] border border-[#F3EFE7]">
    <Icon className={`w-4 h-4 ${color} mb-0.5`} />
    <span className="text-lg font-bold text-[#1E2A33]">{count}</span>
    <span className="text-xs text-[#5C6670]">{label}</span>
  </div>
);

// ── Contact picker ────────────────────────────────────────────────────────────
const ContactPicker = ({ selectedIds, onChange }) => {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contactsAPI.list({}).then(r => {
      setContacts(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = contacts.filter(c =>
    !search ||
    c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => onChange(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium text-[#1E2A33]">
          Recipients ({selectedIds.length} selected)
        </Label>
        <div className="flex gap-2">
          <button onClick={() => onChange(filtered.map(c => c._id))} className="text-xs text-[#A62B1F] hover:underline">All</button>
          <button onClick={() => onChange([])} className="text-xs text-[#5C6670] hover:underline">None</button>
        </div>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6670]" />
        <Input placeholder="Search contacts…" className="pl-8 h-8 text-sm" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="border border-[#E3DDD1] rounded-lg max-h-52 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-[#5C6670] p-3 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#5C6670] p-3 text-center">No contacts found</p>
        ) : filtered.map(c => {
          const checked = selectedIds.includes(c._id);
          return (
            <label key={c._id} className={`flex items-center gap-3 px-3 py-2 hover:bg-[#FBF8F2] cursor-pointer border-b border-[#F3EFE7] last:border-0 ${checked ? 'bg-[#F8EFED]' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(c._id)} className="h-3.5 w-3.5 rounded accent-[#A62B1F]" />
              <Avatar className="h-7 w-7 flex-shrink-0">
                <AvatarFallback className="text-xs bg-[#F7ECEA] text-[#A62B1F]">
                  {(c.full_name || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1E2A33] truncate">{c.full_name}</p>
                <p className="text-xs text-[#5C6670] truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</p>
              </div>
              {checked && <CheckCircle2 className="w-3.5 h-3.5 text-[#A62B1F] flex-shrink-0" />}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ── Artwork uploader ──────────────────────────────────────────────────────────
const ArtworkUploader = ({ value, onChange }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value && !value.startsWith('blob') ? 'upload' : 'upload');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await promoAPI.uploadArtwork(fd);
      onChange(socialMediaAPI.serveUrl(r.data.id));
      toast.success('Artwork uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-sm">Artwork</Label>
        <div className="flex gap-1">
          {[{ id: 'upload', icon: Upload, label: 'Upload' }, { id: 'url', icon: Link, label: 'URL' }].map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                mode === m.id ? 'bg-[#1E2A33] text-white border-[#1E2A33]' : 'text-[#5C6670] border-[#E3DDD1] hover:border-[#1E2A33]'
              }`}
            >
              <m.icon className="w-3 h-3" />{m.label}
            </button>
          ))}
        </div>
      </div>

      {value && (
        <div className="mb-2 flex items-center gap-2">
          <img src={value} alt="Artwork preview" className="w-14 h-14 rounded-lg object-cover border border-[#E3DDD1]" />
          <button type="button" onClick={() => onChange('')} className="text-xs text-red-500 hover:underline">Remove</button>
        </div>
      )}

      {mode === 'upload' ? (
        <>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full border-dashed text-[#5C6670]"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {uploading ? 'Uploading…' : 'Choose image file'}
          </Button>
        </>
      ) : (
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="https://…"
          className="text-sm"
        />
      )}
    </div>
  );
};

// ── Email preview HTML ────────────────────────────────────────────────────────
// Generates a preview matching what recipients will receive.
const buildPreviewHtml = (campaign) => {
  const btnColor = campaign.button_color || '#A62B1F';
  const LISTEN_BTN = `
    <div style="margin:24px 0">
      <a href="#" style="display:block;background:${btnColor};color:#fff;font-weight:800;
         font-size:17px;padding:18px 32px;border-radius:12px;text-decoration:none;
         text-align:center;box-shadow:0 4px 14px rgba(0,0,0,0.18)">
        🎧 Stream &amp; Download
      </a>
      <p style="font-size:11px;color:#9AA3AB;margin:6px 0 0;text-align:center">(Unique link per recipient added on send)</p>
    </div>`;

  if (campaign.body_html) {
    let html = campaign.body_html;
    if (html.includes('{{artwork_url}}')) {
      const artImg = campaign.artwork_url
        ? `<img src="${campaign.artwork_url}" alt="Artwork" style="width:220px;height:220px;object-fit:cover;border-radius:12px;display:block;margin:0 auto 24px"/>`
        : '';
      html = html.replace('{{artwork_url}}', artImg);
    }
    if (html.includes('{{listen_url}}')) {
      return html.replace('{{listen_url}}', LISTEN_BTN);
    }
    if (html.includes('</body>')) {
      return html.replace('</body>', LISTEN_BTN + '</body>');
    }
    return html + LISTEN_BTN;
  }

  // Default template
  const artwork = campaign.artwork_url
    ? `<img src="${campaign.artwork_url}" alt="Artwork" style="width:220px;height:220px;object-fit:cover;border-radius:12px;display:block;margin:0 auto 24px"/>`
    : '';
  const message = campaign.message
    ? `<p style="font-size:15px;color:#5C6670;margin:0 0 24px;line-height:1.6">${campaign.message}</p>`
    : '';

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"></head>
<body style="font-family:'Manrope',Arial,sans-serif;background:#FBF8F2;margin:0;padding:40px 20px">
  <div style="max-width:520px;margin:0 auto;background:#fff;border-radius:16px;padding:40px;box-shadow:0 2px 16px rgba(0,0,0,0.08)">
    <p style="font-size:13px;color:#A62B1F;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;margin:0 0 8px">New Release</p>
    ${artwork}
    <h1 style="font-size:26px;font-weight:800;color:#1E2A33;margin:0 0 4px">${campaign.track_title || 'Track Title'}</h1>
    <p style="font-size:16px;color:#5C6670;margin:0 0 24px">${campaign.artist || 'Artist'}</p>
    ${message}
    ${LISTEN_BTN}
    <p style="font-size:12px;color:#CFC8BB;margin:32px 0 0;border-top:1px solid #F3EFE7;padding-top:16px">
      Sent to [recipient] via Stagehead OS · Anyone Music Records
    </p>
  </div>
</body></html>`;
};

// ── Campaign form fields (shared by Create + Edit sheets) ─────────────────────
const CampaignFormFields = ({ form, setForm, showTemplatePicker, setShowTemplatePicker, sequences = [] }) => (
  <>
    <div>
      <Label className="text-sm">Campaign Name *</Label>
      <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="e.g. Summer Drop 2025" className="mt-1" required />
    </div>

    <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
      <div>
        <Label className="text-sm">Send from</Label>
        <select
          value={form.sequence_id}
          onChange={e => setForm(f => ({ ...f, sequence_id: e.target.value }))}
          className="mt-1 w-full h-9 rounded-md border border-[#E3DDD1] bg-white px-3 text-sm text-[#1E2A33] focus:outline-none focus:ring-2 focus:ring-[#A62B1F]"
        >
          <option value="">Auto-select (first available)</option>
          {sequences.map(s => (
            <option key={s.id || s._id} value={s.id || s._id}>
              {s.smtp_from_email ? `${s.smtp_from_email} (${s.name})` : (s.name || s.smtp_username)}
            </option>
          ))}
        </select>
        {sequences.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">No email accounts found. Go to Email Campaigns, create or edit a campaign, and fill in the Sender Email Settings.</p>
        )}
      </div>
      <div className="w-28">
        <Label className="text-sm">Daily Limit</Label>
        <Input
          type="number"
          min={1}
          max={500}
          value={form.daily_limit}
          onChange={e => setForm(f => ({ ...f, daily_limit: parseInt(e.target.value) || 50 }))}
          className="mt-1 text-sm"
        />
      </div>
    </div>

    <Separator />
    <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide">Track Info</p>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-sm">Track Title *</Label>
        <Input value={form.track_title} onChange={e => setForm(f => ({ ...f, track_title: e.target.value }))}
          placeholder="Track name" className="mt-1" required />
      </div>
      <div>
        <Label className="text-sm">Artist *</Label>
        <Input value={form.artist} onChange={e => setForm(f => ({ ...f, artist: e.target.value }))}
          placeholder="Artist name" className="mt-1" required />
      </div>
    </div>

    <div>
      <Label className="text-sm">Stream URL *</Label>
      <Input value={form.stream_url} onChange={e => setForm(f => ({ ...f, stream_url: e.target.value }))}
        placeholder="https://anyonemusic.com/tracks/your-track.mp3" className="mt-1" required />
      <p className="text-xs text-[#9AA3AB] mt-1">Direct audio URL for streaming</p>
    </div>

    <div>
      <Label className="text-sm">WAV Download URL</Label>
      <Input value={form.wav_url} onChange={e => setForm(f => ({ ...f, wav_url: e.target.value }))}
        placeholder="Leave blank to use stream URL" className="mt-1" />
    </div>

    <div>
      <Label className="text-sm">anyonemusic.com Landing Page URL</Label>
      <Input value={form.listen_page_url} onChange={e => setForm(f => ({ ...f, listen_page_url: e.target.value }))}
        placeholder="https://anyonemusicrecords.com/promo/track-name" className="mt-1" />
      <p className="text-xs text-[#9AA3AB] mt-1">
        The email button links here with <code className="bg-[#F3EFE7] px-1 rounded">?ref=TOKEN</code> appended. Your page reads the token and fires engagement events back to Stagehead OS. Leave blank to use the built-in listen page.
      </p>
    </div>

    <ArtworkUploader value={form.artwork_url} onChange={v => setForm(f => ({ ...f, artwork_url: v }))} />

    <div>
      <Label className="text-sm">Stream &amp; Download Button Color</Label>
      <div className="flex items-center gap-3 mt-1">
        <input
          type="color"
          value={form.button_color || '#A62B1F'}
          onChange={e => setForm(f => ({ ...f, button_color: e.target.value }))}
          className="h-9 w-12 rounded-md border border-[#E3DDD1] cursor-pointer p-0.5 bg-white"
        />
        <div
          className="flex-1 h-9 flex items-center justify-center rounded-xl text-white text-sm font-bold shadow-md"
          style={{ background: form.button_color || '#A62B1F', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}
        >
          🎧 Stream &amp; Download
        </div>
      </div>
    </div>

    <Separator />
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm">Email Body</Label>
        <Button type="button" variant="outline" size="sm"
          className="text-xs text-[#A62B1F] border-[#E7C3BC] hover:bg-[#F7ECEA]"
          onClick={() => setShowTemplatePicker(true)}>
          <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" />Choose Template
        </Button>
      </div>
      <p className="text-xs text-[#9AA3AB] mb-2">
        Compose your email. Use <code className="bg-[#F3EFE7] px-1 rounded">{'{{listen_url}}'}</code> to place the Stream &amp; Download button anywhere, or <code className="bg-[#F3EFE7] px-1 rounded">{'{{artwork_url}}'}</code> to embed the cover art image. Both are injected automatically at the bottom if not placed.
      </p>
      <EmailEditor
        value={form.body_html}
        onChange={html => setForm(f => ({ ...f, body_html: html }))}
        placeholder="Write your promo email, or choose a template above…"
      />
      <TemplatePickerDialog
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={html => { setForm(f => ({ ...f, body_html: html })); setShowTemplatePicker(false); }}
      />
    </div>
  </>
);

// ── Blank form ────────────────────────────────────────────────────────────────
const blankForm = () => ({ title: '', track_title: '', artist: '', stream_url: '', wav_url: '', artwork_url: '', listen_page_url: '', body_html: '', sequence_id: '', button_color: '#A62B1F', daily_limit: 50 });

const formFromCampaign = (c) => ({
  title: c.title || '',
  track_title: c.track_title || '',
  artist: c.artist || '',
  stream_url: c.stream_url || '',
  wav_url: c.wav_url || '',
  artwork_url: c.artwork_url || '',
  listen_page_url: c.listen_page_url || '',
  body_html: c.body_html || '',
  sequence_id: c.sequence_id || '',
  button_color: c.button_color || '#A62B1F',
  daily_limit: c.daily_limit ?? 50,
});

// ── Main panel ────────────────────────────────────────────────────────────────
export default function PromoCampaignPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sequences, setSequences] = useState([]);

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(blankForm());
  const [creating, setCreating] = useState(false);
  const [showCreateTemplatePicker, setShowCreateTemplatePicker] = useState(false);

  // Edit
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [showEditTemplatePicker, setShowEditTemplatePicker] = useState(false);

  // Preview
  const [showPreview, setShowPreview] = useState(false);

  // Send
  const [sending, setSending] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const r = await promoAPI.list();
      setCampaigns(r.data || []);
    } catch {
      toast.error('Failed to load promo campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    sequencesAPI.list().then(r => {
      // Only show sequences that have SMTP configured
      setSequences((r.data || []).filter(s => s.smtp_host));
    }).catch(() => {});
  }, []);

  const openCampaign = async (c) => {
    try {
      const r = await promoAPI.get(c.id);
      setSelectedCampaign(r.data);
      setSelectedContactIds([]);
    } catch {
      toast.error('Failed to load campaign');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title || !createForm.track_title || !createForm.artist || !createForm.stream_url) {
      toast.error('Fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await promoAPI.create({
        title: createForm.title,
        track_title: createForm.track_title,
        artist: createForm.artist,
        stream_url: createForm.stream_url,
        wav_url: createForm.wav_url || createForm.stream_url,
        artwork_url: createForm.artwork_url || undefined,
        listen_page_url: createForm.listen_page_url || undefined,
        body_html: createForm.body_html || undefined,
        sequence_id: createForm.sequence_id || undefined,
        button_color: createForm.button_color || '#A62B1F',
        daily_limit: createForm.daily_limit || 50,
      });
      toast.success('Campaign created');
      setCreateForm(blankForm());
      setShowCreate(false);
      load();
    } catch {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = () => {
    if (!selectedCampaign) return;
    setEditForm(formFromCampaign(selectedCampaign));
    setShowEdit(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.track_title || !editForm.artist || !editForm.stream_url) {
      toast.error('Fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await promoAPI.update(selectedCampaign.id, {
        title: editForm.title,
        track_title: editForm.track_title,
        artist: editForm.artist,
        stream_url: editForm.stream_url,
        wav_url: editForm.wav_url || editForm.stream_url,
        artwork_url: editForm.artwork_url || '',
        listen_page_url: editForm.listen_page_url || '',
        body_html: editForm.body_html || '',
        sequence_id: editForm.sequence_id || null,
        button_color: editForm.button_color || '#A62B1F',
        daily_limit: editForm.daily_limit || 50,
      });
      toast.success('Campaign saved');
      setShowEdit(false);
      // Refresh both detail + list
      const updated = await promoAPI.get(selectedCampaign.id);
      setSelectedCampaign(updated.data);
      load();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!selectedCampaign || selectedContactIds.length === 0) return;
    setSending(true);
    try {
      const r = await promoAPI.send(selectedCampaign.id, {
        contact_ids: selectedContactIds,
        frontend_base_url: window.location.origin,
      });
      const { sent, errors: errs, queued } = r.data;
      if (queued > 0) toast.success(`Sent to ${sent} today — ${queued} more queued and will send automatically tomorrow 🎵`);
      else if (errs?.length) toast.warning(`Sent to ${sent}, ${errs.length} failed`);
      else toast.success(`Sent to ${sent} recipient${sent !== 1 ? 's' : ''} 🎵`);
      setSelectedContactIds([]);
      const updated = await promoAPI.get(selectedCampaign.id);
      setSelectedCampaign(updated.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await promoAPI.delete(deleteTarget.id);
      toast.success('Campaign deleted');
      setDeleteTarget(null);
      if (selectedCampaign?.id === deleteTarget.id) setSelectedCampaign(null);
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = campaigns.filter(c =>
    !search ||
    c.track_title?.toLowerCase().includes(search.toLowerCase()) ||
    c.artist?.toLowerCase().includes(search.toLowerCase()) ||
    c.title?.toLowerCase().includes(search.toLowerCase())
  );

  // Campaign to preview (use edit form if sheet open, otherwise selected)
  const previewSource = showEdit ? editForm : selectedCampaign;

  return (
    <div className="flex h-[calc(100vh-120px)] gap-6 w-full">

      {/* ── Left: campaign list ─────────────────────────────────────────── */}
      <div className="w-[380px] flex flex-col gap-3">
        <Button
          style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
          onClick={() => { setCreateForm(blankForm()); setShowCreate(true); }}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />New Promo Campaign
        </Button>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
          <Input placeholder="Search campaigns…" className="pl-9 text-sm" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <ScrollArea className="flex-1">
          <div className="space-y-2 pr-1">
            {loading ? (
              <p className="text-center text-[#5C6670] py-8 text-sm">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-center text-[#5C6670] py-8 text-sm">
                {campaigns.length === 0 ? 'No promo campaigns yet' : 'No matches'}
              </p>
            ) : filtered.map(c => {
              const isSelected = selectedCampaign?.id === c.id;
              const stats = c.stats || {};
              return (
                <Card
                  key={c.id}
                  onClick={() => openCampaign(c)}
                  className={`cursor-pointer transition-all hover:shadow-md group ${isSelected ? 'border-[#A62B1F] shadow-sm' : 'hover:border-[#A62B1F]'}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {c.artwork_url ? (
                        <img src={c.artwork_url} alt="Art" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center flex-shrink-0">
                          <Music className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-[#1E2A33] text-sm truncate" style={{ fontFamily: 'Manrope' }}>
                            {c.track_title}
                          </p>
                          <Badge variant="outline" className={`text-xs flex-shrink-0 ${c.status === 'sent' ? 'border-green-300 bg-green-50 text-green-700' : 'border-[#E3DDD1] text-[#5C6670]'}`}>
                            {c.status === 'sent' ? '✓' : 'Draft'}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#5C6670] truncate">{c.artist}</p>
                        {c.status === 'sent' && (
                          <div className="flex gap-3 mt-1 text-xs text-[#9AA3AB]">
                            <span>👥 {stats.total || 0}</span>
                            <span>▶ {stats.played || 0}</span>
                            <span>♥ {stats.liked || 0}</span>
                            <span>⬇ {stats.downloaded || 0}</span>
                          </div>
                        )}
                      </div>
                      <button
                        className="text-[#CFC8BB] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                        onClick={e => { e.stopPropagation(); setDeleteTarget(c); }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* ── Right: campaign detail ──────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        {!selectedCampaign ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-white" />
              </div>
              <p className="text-lg font-bold text-[#1E2A33] mb-1" style={{ fontFamily: 'Manrope' }}>
                Select a campaign
              </p>
              <p className="text-sm text-[#5C6670]">
                Choose a promo campaign to view details and send to contacts.
              </p>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="space-y-5 pr-2 pb-8">

              {/* Campaign header */}
              <div className="flex items-start gap-4">
                {selectedCampaign.artwork_url ? (
                  <img src={selectedCampaign.artwork_url} alt="Art" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center flex-shrink-0">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h2 className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                      {selectedCampaign.track_title}
                    </h2>
                    <Badge variant="outline" className={
                      selectedCampaign.status === 'sent' ? 'border-green-300 bg-green-50 text-green-700' :
                      selectedCampaign.status === 'sending' ? 'border-blue-300 bg-blue-50 text-blue-700' :
                      'border-[#E3DDD1] text-[#5C6670]'
                    }>
                      {selectedCampaign.status === 'sent' ? '✓ Sent' :
                       selectedCampaign.status === 'sending' ? '⏳ Sending…' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-sm text-[#5C6670]">{selectedCampaign.artist}</p>
                  <p className="text-xs text-[#9AA3AB]">{selectedCampaign.title}</p>
                  {selectedCampaign.sent_at && (
                    <p className="text-xs text-[#9AA3AB] mt-0.5">
                      Last sent {safeFormat(selectedCampaign.sent_at, 'MMM d, yyyy')}
                    </p>
                  )}
                  {/* Action buttons */}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={openEdit} className="text-xs">
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowPreview(true)} className="text-xs">
                      <Eye className="w-3.5 h-3.5 mr-1.5" />Preview Email
                    </Button>
                    <button
                      className="ml-auto text-[#CFC8BB] hover:text-red-400 p-1"
                      onClick={() => setDeleteTarget(selectedCampaign)}
                      title="Delete campaign"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats */}
              {selectedCampaign.status === 'sending' && selectedCampaign.pending_contact_ids?.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                  <span className="text-base">⏳</span>
                  <div>
                    <p className="font-semibold">Sending in progress</p>
                    <p className="text-xs mt-0.5">
                      {selectedCampaign.stats?.total || 0} sent so far · {selectedCampaign.pending_contact_ids.length} queued · continues automatically at 9am UTC each day
                    </p>
                  </div>
                </div>
              )}

              {(selectedCampaign.status === 'sent' || selectedCampaign.status === 'sending') && (
                <div className="grid grid-cols-4 gap-2">
                  <Stat icon={Users} count={selectedCampaign.stats?.total || 0} label="Sent" color="text-[#5C6670]" />
                  <Stat icon={Play} count={selectedCampaign.stats?.played || 0} label="Played" color="text-blue-500" />
                  <Stat icon={Heart} count={selectedCampaign.stats?.liked || 0} label="Liked" color="text-[#A62B1F]" />
                  <Stat icon={Download} count={selectedCampaign.stats?.downloaded || 0} label="Downloaded" color="text-purple-500" />
                </div>
              )}

              <Separator />

              {/* Send to contacts */}
              <div>
                <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>
                  <Send className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                  Send to Contacts
                </p>
                <ContactPicker selectedIds={selectedContactIds} onChange={setSelectedContactIds} />
                <Button
                  className="mt-3 w-full"
                  disabled={selectedContactIds.length === 0 || sending}
                  onClick={handleSend}
                  style={{ backgroundColor: '#A62B1F', color: 'white' }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending…' : `Send to ${selectedContactIds.length} contact${selectedContactIds.length !== 1 ? 's' : ''}`}
                </Button>
              </div>

              {/* Recipients */}
              {selectedCampaign.recipients?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>
                      <Users className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                      Recipients ({selectedCampaign.recipients.length})
                    </p>
                    <div className="space-y-2">
                      {selectedCampaign.recipients.map(r => (
                        <div key={r.id} className="flex items-center gap-3 p-3 border border-[#F3EFE7] rounded-lg bg-[#FBF8F2]">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="text-xs bg-[#F7ECEA] text-[#A62B1F]">
                              {(r.contact_name || '?').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1E2A33] truncate">{r.contact_name}</p>
                            <p className="text-xs text-[#5C6670] truncate">{r.contact_email}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span title="Played" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.played ? 'bg-blue-100 text-blue-600' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}><Play className="w-3 h-3" /></span>
                            <span title="Liked" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.liked ? 'bg-red-100 text-[#A62B1F]' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}><Heart className="w-3 h-3" /></span>
                            <span title="Downloaded" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.downloaded ? 'bg-purple-100 text-purple-600' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}><Download className="w-3 h-3" /></span>
                            <a
                              href={`${window.location.origin}/promo/${r.token}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open recipient's listen page"
                              onClick={e => e.stopPropagation()}
                              className="w-6 h-6 rounded-full bg-[#F3EFE7] flex items-center justify-center text-[#5C6670] hover:text-[#A62B1F] hover:bg-[#F7ECEA]"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* ── Create Campaign Sheet ── */}
      <Sheet open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setCreateForm(blankForm()); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              New Promo Campaign
            </SheetTitle>
            <SheetDescription>Set up a track and compose the email to send to your contacts</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleCreate} className="mt-6 space-y-5">
            <CampaignFormFields
              form={createForm}
              setForm={setCreateForm}
              showTemplatePicker={showCreateTemplatePicker}
              setShowTemplatePicker={setShowCreateTemplatePicker}
              sequences={sequences}
            />
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={creating} style={{ backgroundColor: '#A62B1F', color: 'white' }} className="flex-1">
                {creating ? 'Creating…' : 'Create Campaign'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setCreateForm(blankForm()); }}>
                Cancel
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Edit Campaign Sheet ── */}
      <Sheet open={showEdit} onOpenChange={v => { setShowEdit(v); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              Edit Campaign
            </SheetTitle>
            <SheetDescription>Update track details or recompose the email</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSave} className="mt-6 space-y-5">
            <CampaignFormFields
              form={editForm}
              setForm={setEditForm}
              showTemplatePicker={showEditTemplatePicker}
              setShowTemplatePicker={setShowEditTemplatePicker}
              sequences={sequences}
            />
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} style={{ backgroundColor: '#1E2A33', color: 'white' }} className="flex-1">
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline"
                onClick={() => { setShowPreview(true); }}
                className="text-[#A62B1F] border-[#E7C3BC]"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />Preview
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Email Preview Dialog ── */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E3DDD1] flex-shrink-0">
            <DialogTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              Email Preview — {previewSource?.track_title || 'Campaign'}
            </DialogTitle>
            <p className="text-xs text-[#9AA3AB] mt-1">
              This is how the email will look. The "Stream &amp; Download" button gets a unique link per recipient on send.
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {previewSource && (
              <iframe
                srcDoc={buildPreviewHtml(previewSource)}
                title="Email Preview"
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{deleteTarget?.title}" and all recipient records. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
